// Abholungen und Marktdaten – reine Funktionen für Server UND Oberfläche (docs/integration/bestell-app.md §20, S2 und S3).
// Keine Datenbank, keine Uhr: Zeiten und Zufall kommen von außen.
import { addDays, type IsoDate } from '@shared/time'

// ---- Abholcode ---------------------------------------------------------------------------------------------------------

/** Zeichen des Abholcodes – ohne I, O, 0 und 1 (Verwechslung), dieselben wie in der Bestell-App */
export const PICKUP_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
export const PICKUP_CODE_LENGTH = 6
const CODE = new RegExp(`^[${PICKUP_CODE_ALPHABET}]{${PICKUP_CODE_LENGTH}}$`)

export const isPickupCode = (value: string): boolean => CODE.test(value)

/** Code aus einer Eingabe: Groß- und Kleinschreibung, Leerzeichen und Bindestriche stören nicht. Sonst null. */
export function normalizePickupCode(raw: string | null | undefined): string | null {
  const code = (raw ?? '').replace(/[\s-]+/g, '').toUpperCase()
  return isPickupCode(code) ? code : null
}

/** Inhalt des QR-Codes: `ALTINGER:<Auftrag bzw. Bestellung>:<Code>` (so druckt und liest ihn die Bestell-App) */
export const pickupQrContent = (orderRef: string, code: string): string => `ALTINGER:${orderRef}:${code}`

export interface ScannedPickup {
  code: string
  /** Kennung vor dem Code (Bestellung der App oder Auftrag in Merxo); null bei reiner Code-Eingabe */
  ref: string | null
}

/** Liest einen QR-Inhalt oder einen reinen Code. Alles andere ist kein Abholcode (null). */
export function parsePickupInput(raw: string | null | undefined): ScannedPickup | null {
  const text = (raw ?? '').trim()
  if (text === '') return null
  const match = /^ALTINGER:(.+):([^:]+)$/i.exec(text)
  if (match) {
    const code = normalizePickupCode(match[2])
    return code ? { code, ref: (match[1] ?? '').trim() || null } : null
  }
  const code = normalizePickupCode(text)
  return code ? { code, ref: null } : null
}

/** Zufälliger Code; `pick(n)` liefert eine Zufallszahl von 0 bis n − 1 (Server: crypto.randomInt) */
export function randomPickupCode(pick: (limit: number) => number): string {
  let code = ''
  for (let index = 0; index < PICKUP_CODE_LENGTH; index++) code += PICKUP_CODE_ALPHABET[pick(PICKUP_CODE_ALPHABET.length)]
  return code
}

/** Code in Zweiergruppen für die Anzeige: „K7 4P Q2“ */
export const pickupCodeSpaced = (code: string): string => code.replace(/(.{2})(?=.)/g, '$1 ')

// ---- Sperre nach Fehlversuchen -------------------------------------------------------------------------------------------------

/** höchstens so viele falsche Codes je Person und Stunde … */
export const PICKUP_MAX_FAILURES = 10
export const PICKUP_FAILURE_WINDOW_MS = 3_600_000
/** … danach so lange gesperrt */
export const PICKUP_LOCK_MS = 15 * 60_000
/** Fehlversuche werden nach 24 Stunden aufgeräumt */
export const PICKUP_ATTEMPT_RETENTION_MS = 24 * 3_600_000

// ---- Einteilung der Abholungen ---------------------------------------------------------------------------------------------------

export type PickupBucket = 'overdue' | 'today' | 'tomorrow' | 'later'

export const PICKUP_BUCKET_LABELS: Record<PickupBucket, string> = { overdue: 'Überfällig', today: 'Heute', tomorrow: 'Morgen', later: 'Später' }

/** Einteilung nach Abholtag; ohne Termin gilt „heute“ (die Ware liegt bereit). */
export function pickupBucket(day: IsoDate | null, today: IsoDate): PickupBucket {
  if (!day || day === today) return 'today'
  if (day < today) return 'overdue'
  return day === addDays(today, 1) ? 'tomorrow' : 'later'
}

// ---- Öffnungszeiten und Hinweis (S3) ---------------------------------------------------------------------------------------------

export interface DayHours {
  open: string
  close: string
}

/** Format der Bestell-App: Wochentag 0 (Sonntag) bis 6 (Samstag) → Zeiten; null oder fehlend = geschlossen */
export type OpeningHours = Record<string, DayHours | null>

/** Reihenfolge der Anzeige: Montag bis Sonntag, jeweils mit dem Wochentag der App (0 = Sonntag) */
export const WEEK_ORDER = [
  { day: 1, label: 'Montag', short: 'Mo' },
  { day: 2, label: 'Dienstag', short: 'Di' },
  { day: 3, label: 'Mittwoch', short: 'Mi' },
  { day: 4, label: 'Donnerstag', short: 'Do' },
  { day: 5, label: 'Freitag', short: 'Fr' },
  { day: 6, label: 'Samstag', short: 'Sa' },
  { day: 0, label: 'Sonntag', short: 'So' },
] as const

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/
export const isClockTime = (value: unknown): value is string => typeof value === 'string' && TIME.test(value)

/** Alle sieben Tage, geschlossene als null – so geht es in die Datenbank und in den Spiegel. */
export function fullOpeningHours(input: Record<string, unknown> | null | undefined): OpeningHours {
  const out: OpeningHours = {}
  for (let day = 0; day <= 6; day++) {
    const entry = input?.[String(day)] as { open?: unknown; close?: unknown } | null | undefined
    out[String(day)] = entry && isClockTime(entry.open) && isClockTime(entry.close) ? { open: entry.open, close: entry.close } : null
  }
  return out
}

/** Satz zum Tag der Eingabe, wenn er nicht stimmt: Ende vor Beginn. */
export function openingHoursProblem(hours: OpeningHours): string | null {
  for (const { day, label } of WEEK_ORDER) {
    const entry = hours[String(day)]
    if (entry && entry.close <= entry.open) return `${label}: Das Ende liegt vor dem Beginn.`
  }
  return null
}

/** „08:00–20:00 Uhr“ bzw. „geschlossen“ */
export const dayHoursText = (entry: DayHours | null | undefined): string => (entry ? `${entry.open}–${entry.close} Uhr` : 'geschlossen')

export interface Announcement {
  text: string
  kind: 'info' | 'warning'
  from: IsoDate | null
  until: IsoDate | null
}

/** Der Hinweis ist am Tag sichtbar (von und bis einschließlich). */
export const announcementActive = (announcement: Pick<Announcement, 'from' | 'until'>, today: IsoDate): boolean =>
  (!announcement.from || announcement.from <= today) && (!announcement.until || announcement.until >= today)

/**
 * Startstandort der Seite Abholungen (N5): der gemerkte Standort der Person, solange sie ihn noch sehen darf; sonst der erste
 * Standort (in der Reihenfolge von `places`), an dem heute Abholungen offen sind; sonst der Arbeitsstandort, der erste Markt, der erste.
 */
export function startPickupLocation(input: {
  places: readonly { id: string; kind: string }[]
  remembered: string | null
  activeId: string | null
  rows: readonly { locationId: string; bucket: string; state: string }[]
}): string | null {
  const { places } = input
  if (input.remembered && places.some((place) => place.id === input.remembered)) return input.remembered
  const withOpenToday = new Set(input.rows.filter((row) => row.bucket === 'today' && row.state !== 'done').map((row) => row.locationId))
  return (
    places.find((place) => withOpenToday.has(place.id))?.id ??
    places.find((place) => place.id === input.activeId)?.id ??
    places.find((place) => place.kind === 'store')?.id ??
    places[0]?.id ??
    null
  )
}
